import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plus, Star, WarningCircle, Check } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin → Languages panel. Manages the runtime language list:
 *   • Add (BCP-47 code + display name)
 *   • Toggle enabled/disabled
 *   • Set default (radio)
 *   • Delete (blocked when default OR in-use)
 *
 * Translation files (fr/en/mg JSON) still ship with the frontend; adding
 * a code without one shows a warning chip — codes can be enabled
 * regardless so admins can prep a language while the file is uploaded.
 */
export const LanguagesManagement = () => {
  const [items, setItems] = useState([]);
  const [shipped, setShipped] = useState([]);
  const [defaultCode, setDefaultCode] = useState('fr');
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/languages`, { withCredentials: true });
      setItems(data.items || []);
      setShipped(data.shipped_translations || []);
      setDefaultCode(data.default || 'fr');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load languages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    if (!newCode.trim() || !newName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/languages`,
        { code: newCode.trim().toLowerCase(), name: newName.trim(), enabled: true },
        { withCredentials: true });
      toast.success(`Language '${newCode}' added`);
      setNewCode(''); setNewName('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleEnabled = async (item, enabled) => {
    try {
      await axios.patch(`${API}/admin/languages/${item.code}`,
        { enabled }, { withCredentials: true });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleSetDefault = async (item) => {
    try {
      await axios.post(`${API}/admin/languages/${item.code}/set-default`,
        {}, { withCredentials: true });
      toast.success(`Default set to ${item.name}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/languages/${deleteTarget.code}`,
        { withCredentials: true });
      toast.success(`Language '${deleteTarget.code}' deleted`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="languages-management">
      <div>
        <h2 className="text-lg font-semibold text-text_strong">Langues</h2>
        <p className="text-sm text-text_muted">
          Gérez les langues disponibles dans l'interface, sur la page d'inscription
          et dans les profils utilisateurs.
        </p>
      </div>

      {/* Add new language */}
      <div className="bg-white border border-border rounded-xl p-4 grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end">
        <div>
          <Label className="text-xs">Code (BCP-47)</Label>
          <Input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder="pt"
            className="mt-1 font-mono text-sm"
            maxLength={8}
            data-testid="language-new-code"
          />
        </div>
        <div>
          <Label className="text-xs">Display name</Label>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Português"
            className="mt-1"
            maxLength={80}
            data-testid="language-new-name"
          />
        </div>
        <Button
          onClick={handleCreate}
          disabled={creating || !newCode.trim() || !newName.trim()}
          data-testid="language-add-btn"
        >
          <Plus size={16} className="mr-1" /> Ajouter
        </Button>
      </div>

      {/* Existing languages list */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-text_muted text-sm">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-text_muted text-sm">Aucune langue.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface text-text_muted text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 w-20">Code</th>
                <th className="text-left px-4 py-2">Nom</th>
                <th className="text-left px-4 py-2 w-32">Translation</th>
                <th className="text-left px-4 py-2 w-28">Default</th>
                <th className="text-left px-4 py-2 w-28">Enabled</th>
                <th className="text-right px-4 py-2 w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((lang) => {
                const hasTranslation = lang.has_translation ?? shipped.includes(lang.code);
                const isDefault = lang.code === defaultCode;
                return (
                  <tr key={lang.code} data-testid={`language-row-${lang.code}`}>
                    <td className="px-4 py-3 font-mono">{lang.code}</td>
                    <td className="px-4 py-3">{lang.name}</td>
                    <td className="px-4 py-3">
                      {hasTranslation ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-success-soft text-success border border-success">
                          <Check size={10} weight="bold" /> Disponible
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-warning-soft text-warning border border-warning"
                          title="Pas de fichier de traduction. Les utilisateurs verront les chaînes par défaut (FR)."
                          data-testid={`language-${lang.code}-missing-translation`}
                        >
                          <WarningCircle size={10} weight="bold" /> Manquante
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isDefault ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary border border-primary"
                          data-testid={`language-${lang.code}-default-badge`}
                        >
                          <Star size={10} weight="fill" /> Default
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(lang)}
                          disabled={!lang.enabled}
                          className="text-[11px] text-text_muted hover:text-primary disabled:opacity-40"
                          data-testid={`language-${lang.code}-set-default`}
                          title={lang.enabled ? 'Set as default' : 'Enable first'}
                        >
                          Définir
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={!!lang.enabled}
                        onCheckedChange={(v) => handleToggleEnabled(lang, v)}
                        disabled={isDefault && !lang.enabled === false /* never */ ? true : false}
                        data-testid={`language-${lang.code}-toggle`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(lang)}
                        disabled={isDefault}
                        className="text-text_muted hover:text-danger disabled:opacity-30"
                        data-testid={`language-${lang.code}-delete`}
                        title={isDefault ? 'Cannot delete the default language' : 'Delete'}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="language-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette langue ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>Code <span className="font-mono">{deleteTarget.code}</span> — {deleteTarget.name}.
                Cette action est bloquée si des utilisateurs l'ont sélectionnée. Désactivez plutôt que supprimer si nécessaire.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="language-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-danger text-white hover:bg-danger/90"
              data-testid="language-delete-confirm"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
